import { Builder } from '@builder.io/react'
import { faChevronDown, faChevronUp, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Disclosure, Transition } from '@headlessui/react'
import CdnImage from 'components/util/CdnImage'
import React from 'react'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

enum Widths {
  Full = 'full',
  Half = 'half'
}
enum DropdownType {
  Faq = 'faq',
  Job = 'job'
}

interface DropdownList {
  accordionText: string
  accordionFontColor: string
  accordionBackgroundColor: string
  dropdownText: string
  dropdownFontColor: string
  dropdownBackgroundColor: string
}

interface Props {
  dropdownType: DropdownType
  headingType: HeadingType
  headingClass: HeadingClass
  dropdownList: DropdownList[]
  headingText: string
  headingTextColor: string
  width: Widths
  dropdownIcon: any
  dropdownIconAltText: string
  dropdownOpenIcon: any
  dropdownOpenIconAltText: string
}
const Accordion = ({
  dropdownType,
  headingType,
  headingClass,
  headingText,
  headingTextColor,
  dropdownList,
  width,
  dropdownIcon,
  dropdownIconAltText,
  dropdownOpenIcon,
  dropdownOpenIconAltText
}: Props) => {
  const accordionWidth = width === Widths.Full ? 'w-full' : 'w-1/2'

  const renderDropdownIcon = (open: boolean) => {
    if (dropdownIcon && dropdownOpenIcon) {
      return (
        <>
          {' '}
          {open ? (
            <CdnImage
              className="index-2 height-specifier relative w-full h-full rounded-3xl object-cover"
              url={dropdownIcon}
              alt={dropdownIconAltText}
              steps={[1000]}
            />
          ) : (
            <CdnImage
              className="index-2 height-specifier relative w-full h-full rounded-3xl object-cover"
              url={dropdownOpenIcon}
              alt={dropdownOpenIconAltText}
              steps={[1000]}
            />
          )}
        </>
      )
    } else {
      if (dropdownType === 'job') {
        return (
          <>
            {' '}
            {open ? (
              <FontAwesomeIcon icon={faChevronUp} className={'h-6 w-6 transform text-white'} aria-hidden="true" />
            ) : (
              <FontAwesomeIcon icon={faChevronDown} className={'h-6 w-6 transform text-white'} aria-hidden="true" />
            )}
          </>
        )
      } else if (dropdownType === 'faq') {
        return (
          <>
            {' '}
            {open ? (
              <FontAwesomeIcon icon={faMinus} className={'h-6 w-6 transform text-white'} aria-hidden="true" />
            ) : (
              <FontAwesomeIcon icon={faPlus} className={'h-6 w-6 transform text-white'} aria-hidden="true" />
            )}
          </>
        )
      }
    }
  }

  return (
    <>
      <CustomHeading
        classList="text-center pb-2"
        headingClass={headingClass}
        headingType={headingType}
        color={headingTextColor || '#000000'}
      >
        {headingText}
      </CustomHeading>

      <dl className={`flex flex-col gap-y-4 container px-12 mx-auto ${accordionWidth}`}>
        {dropdownList?.map((dd, i) => (
          <Disclosure as="div" key={i}>
            {({ open }) => (
              <>
                <dt
                  className="relative rounded-2xl lg:rounded-full p-2 text-lg"
                  style={{ backgroundColor: dd.accordionBackgroundColor, zIndex: 1 }}
                >
                  <Disclosure.Button className={`flex w-full items-center justify-between text-left text-primary`}>
                    <div
                      className={`mx-2 lg:ml-2`}
                      style={{ color: dd.accordionFontColor }}
                      dangerouslySetInnerHTML={{ __html: dd.accordionText }}
                    />
                    <span
                      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${!dropdownIcon && !dropdownOpenIcon ? 'bg-primary' : 'bg-none'}`}
                    >
                      {renderDropdownIcon(open)}
                    </span>
                  </Disclosure.Button>
                </dt>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform scale-55 opacity-0"
                  enterTo="transform scale-100 opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform scale-100 opacity-100"
                  leaveTo="transform scale-55 opacity-0"
                >
                  <Disclosure.Panel
                    as="dd"
                    className={`relative -top-5 rounded-b-3xl p-4 pt-8`}
                    style={{ zIndex: 0, backgroundColor: dd.dropdownBackgroundColor }}
                  >
                    <div
                      className=""
                      style={{ color: dd.dropdownFontColor }}
                      dangerouslySetInnerHTML={{ __html: dd.dropdownText }}
                    />
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>
        ))}
      </dl>
    </>
  )
}
export default Accordion
Builder.registerComponent(Accordion, {
  name: 'Accordion',
  inputs: [
    {
      name: 'dropdownType',
      type: 'string',
      enum: ['faq', 'job'],
      defaultValue: 'faq'
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'FAQs'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },

    {
      name: 'dropdownList',
      type: 'list',
      defaultValue: [
        {
          accordionText: 'Whats the best food that we can get?',
          accordionFontColor: '',
          accordionBackgroundColor: '',
          dropdownText: 'Dog food',
          dropdownFontColor: '',
          dropdownBackgroundColor: ''
        }
      ],
      subFields: [
        { name: 'accordionText', type: 'richText', defaultValue: 'Whats the best food that we can get?' },
        { name: 'accordionFontColor', type: 'string', defaultValue: '' },
        { name: 'accordionBackgroundColor', type: 'string', defaultValue: '' },
        { name: 'dropdownText', type: 'richText', defaultValue: 'Dog food' },
        { name: 'dropdownFontColor', type: 'string', defaultValue: '' },
        { name: 'dropdownBackgroundColor', type: 'string', defaultValue: '' }
      ]
    },

    {
      name: 'width',
      type: 'string',
      defaultValue: 'full',
      enum: ['full', 'half']
    },
    {
      name: 'dropdownIcon',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'dropdownIconAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'dropdownOpenIcon',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'dropdownOpenIconAltText',
      type: 'text',
      defaultValue: ''
    }
  ]
})
